import { Endpoint, BaseURL } from "shared/utils/endpoints";
import { HTTP_CLIENT } from "shared/utils/interceptor";
import { store } from "shared/redux/store";

const GetProjectRevisionService = (
  id: number,
  page: number,
  limit: number,
  uploadSortBy?: string,
) => {
  const params: Record<string, any> = { page, limit };
  if (uploadSortBy) params.upload_sort_by = uploadSortBy;
  return HTTP_CLIENT.get(
    Endpoint.revision.getProjectRevision.replace(":project_id", id.toString()),
    { params },
  );
};

const DesignerUploadRevisionService = (payload: any) => {
  return HTTP_CLIENT.post(Endpoint.revision.designerUploadRevision, payload);
};

const DeleteDesignerUploadService = (designerUploadId: number) => {
  return HTTP_CLIENT.delete(
    Endpoint.revision.deleteDesignerUpload.replace(
      ":designer_upload_id",
      designerUploadId.toString(),
    ),
  );
};

const RevisionUploadFileService = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const token = store.getState().root.auth?.token;
  const uploadUrl = `${BaseURL}${Endpoint.revision.revisionUploadFile}`;

  return {
    formData,
    uploadUrl,
    token,
  };
};

const RevisionClientResponseService = (
  revisionId: number,
  payload: FormData,
) => {
  return HTTP_CLIENT.post(
    Endpoint.revision.revisionClientResponse.replace(
      ":revision_id",
      revisionId.toString(),
    ),
    payload,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

const RevisionProjectHistoryService = (
  projectId: number,
  page?: number,
  limit?: number,
) => {
  const params: any = {};
  if (page !== undefined) params.page = page;
  if (limit !== undefined) params.limit = limit;

  return HTTP_CLIENT.get(
    Endpoint.revision.revisionProjectHistory.replace(
      ":project_id",
      projectId.toString(),
    ),
    { params },
  );
};

const DownloadRevisionFilesService = (revisionId: number) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.downloadRevisionFiles.replace(
      ":revision_id",
      revisionId.toString(),
    ),
    {
      responseType: "blob",
    },
  );
};
const GetRevisionUploadsService = (revisionId: number, type: string) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.getRevisionUploads.replace(
      ":revision_id",
      revisionId.toString(),
    ),
    {
      params: { type },
      responseType: "blob",
    },
  );
};
const DownloadDesignerUploadService = (designerUploadId: number) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.downloadDesignerUpload.replace(
      ":designer_upload_id",
      designerUploadId.toString(),
    ),
    { responseType: "blob" },
  );
};

const DownloadRevisionInspirationService = (inspirationId: number) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.downloadRevisionInspiration.replace(
      ":revision_inspiration_id",
      inspirationId.toString(),
    ),
    { responseType: "blob" },
  );
};

const GetProjectRevisionListService = (
  projectId: number,
  page: number,
  limit: number,
) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.getProjectRevisionList.replace(
      ":project_id",
      projectId.toString(),
    ),
    { params: { page, limit } },
  );
};

const GetRevisionFeedbackService = (revisionId: number) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.getRevisionFeedback.replace(
      ":revision_id",
      revisionId.toString(),
    ),
  );
};

const SetRevisionInProgressService = (revisionId: number) => {
  return HTTP_CLIENT.patch(
    Endpoint.revision.setRevisionInProgress.replace(
      ":revision_id",
      revisionId.toString(),
    ),
  );
};

const MarkRevisionViewedService = (revisionId: number) => {
  return HTTP_CLIENT.post(
    Endpoint.revision.markRevisionViewed.replace(
      ":revision_id",
      revisionId.toString(),
    ),
  );
};

const UpdateRevisionInspirationsService = (
  revisionId: number,
  formData: FormData,
) => {
  return HTTP_CLIENT.patch(
    Endpoint.revision.updateRevisionInspirations.replace(
      ":revision_id",
      revisionId.toString(),
    ),
    formData,
  );
};

const GetLatestFeedbackService = (projectId: number) => {
  return HTTP_CLIENT.get(
    Endpoint.revision.getLatestFeedback.replace(
      ":project_id",
      projectId.toString(),
    ),
  );
};

export interface RevisionDetailEdits {
  title?: string;
  designer_comment?: string;
  internal_comment?: string;
}

// Omit a detail to leave it unchanged; send empty markup to clear a comment.
const UpdateRevisionDesignerUploadsService = (
  revisionId: number,
  designer_uploads: { id: number; order: number }[],
  details?: RevisionDetailEdits,
) => {
  return HTTP_CLIENT.patch(
    Endpoint.revision.updateDesignerUploads.replace(
      ":revision_id",
      revisionId.toString(),
    ),
    { designer_uploads, ...(details || {}) },
  );
};

// Persist a revision's shared media sort. Pass null for Default (manual order).
const SetRevisionMediaSortService = (
  revisionId: number,
  media_sort:
    | "name_asc"
    | "name_desc"
    | "modified_asc"
    | "modified_desc"
    | null,
) => {
  return HTTP_CLIENT.patch(
    Endpoint.revision.setRevisionMediaSort.replace(
      ":revision_id",
      revisionId.toString(),
    ),
    { media_sort },
  );
};

// Uploads a single designer file and resolves to its new
// revision_designer_upload_id (the upload endpoint streams progress; we read
// the final chunk). Used by the post-submit "Edit Files" flow.
const UploadDesignerFile = async (file: File): Promise<number> => {
  const { formData, uploadUrl, token } = RevisionUploadFileService(file);
  const response = await fetch(uploadUrl, {
    method: "POST",
    body: formData,
    headers: { ...(token && { authorization: `Bearer ${token}` }) },
  });
  if (!response.ok || !response.body) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fileId: number | undefined;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      try {
        const data = JSON.parse(line.substring(6));
        if (data?.data?.revision_designer_upload_id) {
          fileId = data.data.revision_designer_upload_id;
        }
      } catch {
        /* ignore non-JSON progress lines */
      }
    }
  }
  if (!fileId) throw new Error("No file ID received from server");
  return fileId;
};

export {
  GetProjectRevisionService,
  DesignerUploadRevisionService,
  DeleteDesignerUploadService,
  RevisionUploadFileService,
  RevisionClientResponseService,
  RevisionProjectHistoryService,
  DownloadRevisionFilesService,
  GetRevisionUploadsService,
  DownloadDesignerUploadService,
  DownloadRevisionInspirationService,
  GetProjectRevisionListService,
  GetRevisionFeedbackService,
  SetRevisionInProgressService,
  MarkRevisionViewedService,
  UpdateRevisionInspirationsService,
  GetLatestFeedbackService,
  UpdateRevisionDesignerUploadsService,
  SetRevisionMediaSortService,
  UploadDesignerFile,
};
